import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { type ColumnDef } from "@tanstack/react-table";
import { IconDiscount, IconLink, IconPencil, IconPlus } from "@tabler/icons-react";

import PageHeader from "../../components/common/PageHeader";
import { DataTable, DataTablePagination } from "../../components/ui/data-table";
import { UnderlineTabs } from "../../components/ui/underline-tabs";
import { Button } from "../../components/ui/button";
import { EmptyState } from "../../components/ui/empty-state";
import { Switch } from "../../components/ui/switch";
import { DashboardCard, StatusText } from "@/components/dashboard/dashboard-card";
import { IconButton } from "@/components/dashboard/icon-button";
import { describeMonthChange, MetricPanel, type Metric } from "@/components/dashboard/metric-cell";
import type { Campaign, CampaignOverviewResponse } from "../../types/campaign";
import { campaignService } from "../../services/campaign.service";
import { formatAmount, formatNumber } from "../../utils/formatters";

type StatusTab = "ACTIVE" | "INACTIVE";

function shortDate(value: string) {
  return new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** "18" or "18 of 50" when the campaign has a total usage limit. */
function usesText(campaign: Campaign) {
  const used = formatNumber(Number(campaign.usageCount) || 0);
  const limit = campaign.usageLimit?.match(/\d+/)?.[0];
  return limit ? `${used} of ${formatNumber(Number(limit))}` : used;
}

export default function Campaigns() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<StatusTab>("ACTIVE");
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [overview, setOverview] = useState<CampaignOverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [counts, setCounts] = useState<Record<StatusTab, number | null>>({
    ACTIVE: null,
    INACTIVE: null,
  });
  const [hasError, setHasError] = useState(false);

  const otherTab: StatusTab = tab === "ACTIVE" ? "INACTIVE" : "ACTIVE";

  const loadCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      setHasError(false);
      const response = await campaignService.getCampaigns(page, pageSize, tab);
      setCampaigns(response.data ?? []);
      setTotalPages(response.pagination?.totalPages ?? 1);
      setCounts((prev) => ({ ...prev, [tab]: response.pagination?.total ?? 0 }));
    } catch (error: unknown) {
      console.error("Failed to load campaigns:", error);
      setHasError(true);
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        "Could not load campaigns";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, tab]);

  // The other tab's count, one row is enough to read the total.
  const loadOtherCount = useCallback(async () => {
    try {
      const response = await campaignService.getCampaigns(1, 1, otherTab);
      setCounts((prev) => ({ ...prev, [otherTab]: response.pagination?.total ?? 0 }));
    } catch {
      setCounts((prev) => ({ ...prev, [otherTab]: null }));
    }
  }, [otherTab]);

  const loadOverview = useCallback(async () => {
    try {
      setOverviewLoading(true);
      setOverview(await campaignService.getCampaignOverview());
    } catch (error) {
      console.error("Failed to load campaign overview:", error);
    } finally {
      setOverviewLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  useEffect(() => {
    loadOtherCount();
  }, [loadOtherCount]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const handleToggleStatus = async (campaign: Campaign, active: boolean) => {
    try {
      setUpdatingId(campaign.id);
      await campaignService.toggleCampaignStatus(campaign.id, active);
      toast.success(active ? "Campaign activated" : "Campaign paused");
      await Promise.all([loadCampaigns(), loadOtherCount()]);
    } catch (error: unknown) {
      console.error("Failed to toggle campaign status:", error);
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        "Could not update the campaign";
      toast.error(message);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleCopyLink = async (campaign: Campaign) => {
    try {
      await navigator.clipboard.writeText(campaign.link);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy the link");
    }
  };

  const openEdit = (campaign: Campaign) =>
    navigate(`/brand/campaigns/${campaign.id}/edit`, { state: { campaign } });
  const openNew = () => navigate("/brand/campaigns/new");

  const columns = useMemo<ColumnDef<Campaign>[]>(
    () => [
      {
        accessorKey: "code",
        header: "Code",
        meta: { fit: true },
        cell: ({ row }) => (
          <span className="font-medium text-foreground">{row.original.code}</span>
        ),
      },
      {
        accessorKey: "title",
        header: "Campaign",
        cell: ({ row }) => (
          <span className="block max-w-[16rem] truncate text-foreground" title={row.original.title}>
            {row.original.title}
          </span>
        ),
      },
      {
        accessorKey: "tag",
        header: "Discount",
        cell: ({ row }) => (
          <span className="block max-w-[14rem] truncate text-muted-foreground" title={row.original.tag}>
            {row.original.tag}
          </span>
        ),
      },
      {
        accessorKey: "totalRevenue",
        header: "Revenue",
        meta: { fit: true },
        cell: ({ row }) => (
          <span className="tabular-nums font-medium text-foreground">
            {formatAmount(Number(row.original.totalRevenue) || 0)}
          </span>
        ),
      },
      {
        accessorKey: "usageCount",
        header: "Uses",
        meta: { fit: true },
        cell: ({ row }) => <span className="tabular-nums text-foreground">{usesText(row.original)}</span>,
      },
      {
        accessorKey: "createdAt",
        header: "Created",
        meta: { fit: true },
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-muted-foreground">{shortDate(row.original.createdAt)}</span>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        meta: { fit: true },
        cell: ({ row }) => <StatusText status={row.original.status} />,
      },
      {
        id: "actions",
        header: "",
        meta: { fit: true, cellClassName: "text-right", skeleton: <span /> },
        cell: ({ row }) => {
          const campaign = row.original;
          const busy = updatingId === campaign.id;
          return (
            <span className="inline-flex items-center gap-1">
              <IconButton
                label="Copy link"
                icon={<IconLink stroke={1.75} />}
                onClick={() => void handleCopyLink(campaign)}
              />
              <IconButton
                label="Edit campaign"
                icon={<IconPencil stroke={1.75} />}
                onClick={() => openEdit(campaign)}
              />
              <Switch
                size="sm"
                className="ml-1"
                checked={campaign.status === "ACTIVE"}
                disabled={busy || campaign.status === "EXPIRED"}
                onCheckedChange={(on) => void handleToggleStatus(campaign, on)}
                aria-label={campaign.status === "ACTIVE" ? "Pause campaign" : "Activate campaign"}
              />
            </span>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updatingId]
  );

  // Monthly figures, oldest first, for the strip.
  const months = useMemo(() => (overview?.monthlyStats ?? []).slice(0, 12).reverse(), [overview]);
  const monthKey = (i: number) => String(i).padStart(2, "0");
  const monthLabel = (key: string) => {
    const m = months[Number(key)];
    return m ? `${m.month} ${m.year}` : key;
  };
  const revenueSeries = Object.fromEntries(months.map((m, i) => [monthKey(i), m.revenue]));
  const ordersSeries = Object.fromEntries(months.map((m, i) => [monthKey(i), m.salesCount]));
  const current = overview?.currentMonth;
  const lastMonth = months.length >= 2 ? months[months.length - 2] : undefined;

  const metrics: Metric[] = [
    {
      id: "campaign-revenue",
      label: "Revenue this month",
      value: formatAmount(current?.revenue ?? 0),
      change: describeMonthChange(current?.revenue ?? 0, lastMonth?.revenue, formatAmount),
      series: revenueSeries,
      unit: "revenue",
      format: formatAmount,
      labelFor: monthLabel,
    },
    {
      id: "campaign-orders",
      label: "Orders this month",
      value: formatNumber(current?.salesCount ?? 0),
      change: describeMonthChange(current?.salesCount ?? 0, lastMonth?.salesCount, formatNumber),
      series: ordersSeries,
      unit: "order",
      labelFor: monthLabel,
    },
    {
      id: "campaign-active-count",
      label: "Active campaigns",
      value: counts.ACTIVE === null ? "—" : formatNumber(counts.ACTIVE),
      change:
        counts.INACTIVE === null
          ? undefined
          : `${formatNumber(counts.INACTIVE)} ${counts.INACTIVE === 1 ? "inactive" : "inactive"}`,
    },
  ];

  // Links from the onboarding tour and older bookmarks used ?tab=create.
  const tabParam = searchParams.get("tab");
  if (tabParam === "create") {
    const editId = searchParams.get("editId");
    return <Navigate to={editId ? `/brand/campaigns/${editId}/edit` : "/brand/campaigns/new"} replace />;
  }

  const nothingAtAll =
    !loading && !hasError && campaigns.length === 0 && (counts[otherTab] ?? 0) === 0;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Campaigns"
        customActions={[
          {
            id: "create-campaign",
            label: "New campaign",
            variant: "primary",
            icon: <IconPlus size={16} stroke={2} />,
            onClick: openNew,
          },
        ]}
      />

      <MetricPanel
        title="Activity"
        metrics={metrics}
        columns="lg:grid-cols-3"
        loading={overviewLoading}
      />

      <DashboardCard
        title="Campaigns"
        bodyClassName="px-3 pb-3"
        fixed={
          <UnderlineTabs<StatusTab>
            tabs={[
              { value: "ACTIVE", label: "Active", id: "active-campaigns", count: counts.ACTIVE },
              { value: "INACTIVE", label: "Inactive", count: counts.INACTIVE },
            ]}
            value={tab}
            onValueChange={(next) => {
              setTab(next);
              setPage(1);
            }}
            className="mx-5 mb-2"
          />
        }
      >
        {hasError ? (
          <EmptyState
            bordered={false}
            icon={<IconDiscount stroke={1.75} />}
            title="Could not load campaigns"
            body="Check your connection and try again"
            action={
              <Button variant="outline" onClick={() => void loadCampaigns()}>
                Try again
              </Button>
            }
          />
        ) : nothingAtAll ? (
          <EmptyState
            bordered={false}
            icon={<IconDiscount stroke={1.75} />}
            title="No campaigns yet"
            body="Create a discount code your affiliates can share"
            action={<Button onClick={openNew}>New campaign</Button>}
          />
        ) : (
          <>
            <DataTable
              columns={columns}
              data={campaigns}
              isLoading={loading}
              getRowId={(row) => row.id}
              onRowClick={openEdit}
              emptyText={tab === "ACTIVE" ? "No active campaigns" : "No inactive campaigns"}
            />
            {(counts[tab] ?? 0) > 0 && (
              <DataTablePagination
                page={page}
                totalPages={totalPages}
                total={counts[tab] ?? 0}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPage(1);
                }}
                pageSizeOptions={[10, 20, 50, 100]}
                noun="campaigns"
                className="-mx-3 -mb-3 mt-3"
              />
            )}
          </>
        )}
      </DashboardCard>
    </div>
  );
}
